import { get } from "lodash";

import { COLORS, SHADOW } from "@treelab/panda";

import Chart from "./Chart";

import { IChartFormValue } from "../types/form-type";
import { ITableData, IWrokspaceData } from "../types/table-type";
import { ITooltipFormatterParams } from "../types/echarts-type";
import { ChartType } from "../types/global-type";

import { LEGEND_HEIGHT, LEGEND_PAGE_ICON_PATH, LEGEND_PAGE_ICON_SIZE } from "../constants/chart";

import {
  getXAxisData,
  getLineTooltipDom,
  getXAxisLabelOptionByCategory,
  getGridClientRect,
  getTooltipPosition,
} from "../utils/option_main";

abstract class BasicLineChart extends Chart {
  public getTooltip() {
    const canvasWidth = this.getCanvasWidth();
    const tooltip = {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      borderWidth: 0,
      padding: [10, 12],
      extraCssText: SHADOW.overlay1,
      formatter: (params: ITooltipFormatterParams | ITooltipFormatterParams[], ticket: string) => {
        const newParams = this.convertTooltipParams(params);
        const dom = getLineTooltipDom(newParams, canvasWidth, this.formatValue, this.calculation.isGroup);
        return dom;
      },
      axisPointer: {
        lineStyle: {
          color: "rgba(0, 0, 0, 0.16)",
        },
      },
      position: getTooltipPosition,
    };
    return tooltip;
  }

  public getLegend(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    const { isShowLegend } = formValue;
    const legend = isShowLegend
      ? {
          type: "scroll",
          show: true,
          icon: "path://M0 1C0 0.447715 0.447715 0 1 0H7C7.55228 0 8 0.447715 8 1C8 1.55228 7.55228 2 7 2H1C0.447715 2 0 1.55228 0 1Z",
          width: this.getLegendContainerWidth(),
          height: LEGEND_HEIGHT,
          top: this.isExpand ? 20 : 0,
          left: this.isExpand ? 32 : 24,
          itemWidth: 8,
          itemHeight: 2,
          itemGap: 16,
          textStyle: {
            color: `${COLORS.grey}`,
            backgroundColor: "transparent",
            fontSize: 12,
            lineHeight: 16,
            overflow: "truncate",
          },
          formatter: (name: any) => {
            return this.basicFormatLegend(name);
          },
          pageIconColor: "rgba(131, 134, 143, 1)",
          pageIconInactiveColor: "rgba(162, 165, 177, 0.6)",
          pageTextStyle: {
            color: "rgba(131, 134, 143, 1)",
            fontWeight: 400,
          },
          pageIcons: LEGEND_PAGE_ICON_PATH,
          pageIconSize: LEGEND_PAGE_ICON_SIZE,
        }
      : { show: false };
    return legend;
  }

  public getXAxis(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    return {};
  }

  public calculationOther() {}

  public getYAxis(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    const { isShowYAxis = false } = formValue;
    return isShowYAxis
      ? {
          type: "value",
          name: this.getYAxisName(),
          nameGap: 8,
          nameTextStyle: {
            align: "right",
            color: COLORS.greyLight1,
          },
          show: true,
          splitLine: {
            show: true,
            lineStyle: {
              type: [2, 2],
            },
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: COLORS.greyLight2,
            },
          },
          axisLabel: {
            color: COLORS.grey,
            fontSize: 12,
            formatter: this.formatYAxisLabel,
          },
        }
      : { show: false };
  }

  public getGrid() {
    const { isShowYAxis, isShowLegend } = this.formValue;
    const data = getXAxisData(get(this.datasetObj, "[0].source"));
    const isGlobalFormat = this.calculation.checkGlobalFormat();
    const xAxisLabel = getXAxisLabelOptionByCategory(
      data,
      this.getCanvasWidth(),
      this.getCanvasHeight(),
      this.isExpand,
      !!isShowYAxis,
      isGlobalFormat ? this.formatDimension : undefined
    );
    const { rotate } = xAxisLabel as { rotate: number };
    const grid = {
      ...getGridClientRect(
        this.isExpand,
        ChartType.BASIC_BAR,
        !!rotate,
        !!isShowYAxis,
        isShowLegend && this.calculation.isGroup
      ),
    };
    grid.right += this.isExpand ? 40 : !!rotate ? 0 : 0;
    return grid;
  }

  public checkChartOptionError() {
    return null;
  }

  /**
   * 尝试 使用和 bar 一样的逻辑
   * 结果 ECharts 输出警告 Visual map on line style only support x or y dimension.
   * 而 折线图 可以通过设置 series 的  itemStyle.color 使得 点 的颜色使用 option
   * 但 折线图 的 series.lineStyled.color 不支持函数 所以无法动态设置颜色
   * 和产品沟通后 决定不给 折线图 开放使用 option 颜色功能
   */
  public getVisualMap(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    return null;
  }
}

export default BasicLineChart;
